import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { IssuePinItem } from "@ServerTypes/issues/issues.schema";

export type { IssuePinItem };

type IssuesPinDataState = Record<string, IssuePinItem>;

/**
 * The payload is effectively `unknown`: the one dispatch site (bcfData.actions `getIssuesPinData`)
 * hands `response.data.result` over verbatim, and that field is declared by `issueListResponse`
 * (full issue items) even though the endpoint answers with pin rows. Nothing here can prove the
 * element shape, so the only member this state actually needs — the `id` it is keyed by — is the
 * one that gets checked.
 */
function isPinItem(value: unknown): value is IssuePinItem {
  return typeof value === "object" && value !== null && "id" in value && typeof value.id === "string";
}

const issuesPinDataSlice = createSlice({
  name: "issuesPinData",
  initialState: {} as IssuesPinDataState,
  reducers: {
    setIssuesPinData: (_state: IssuesPinDataState, action: PayloadAction<unknown>) => {
      const rows: unknown[] = Array.isArray(action.payload) ? action.payload : [];
      return rows.reduce<IssuesPinDataState>(
        (prev, curr) => (isPinItem(curr) ? { ...prev, [curr.id]: curr } : prev),
        {},
      );
    },
    mergeIssuesPinData: (state: IssuesPinDataState, action: PayloadAction<IssuePinItem>) => {
      state[action.payload.id] = { ...action.payload };
    },
    deleteIssuesPinData: (state: IssuesPinDataState, action: PayloadAction<string[]>) => {
      action.payload.forEach((issue: string) => {
        delete state[issue];
      });
    },
  },
});

export const { setIssuesPinData, mergeIssuesPinData, deleteIssuesPinData } = issuesPinDataSlice.actions;
export default issuesPinDataSlice.reducer;
